import type { StoreListItem, StoreDetail } from "@/types";

/**
 * Comment afficher le coût de livraison d'un magasin.
 *
 * Mesuré sur l'API, aux MÊMES coordonnées (Bab Ezzouar) :
 *
 *   GET /guest/stores   Chicken Crousty  delivery_charge = 0    distance = 1466
 *   GET /stores         Chicken Crousty  delivery_charge = 220  distance = 1466
 *
 * L'endpoint invité renvoie la distance mais PAS les frais : zéro pour les 5
 * magasins testés, et zéro pour les 513 du catalogue. L'endpoint authentifié,
 * lui, facture 220, 285, 305 DA — et aucun de ses 50 magasins échantillonnés
 * n'est à zéro.
 *
 * Or le code lisait `delivery_charge === 0` comme « livraison offerte ». Tout
 * visiteur non connecté se voyait donc promettre la gratuité sur CHAQUE
 * magasin, et chaque fiche indexée l'annonçait à Google. Une promesse de prix
 * qu'on ne peut pas tenir.
 *
 * Règle retenue : **zéro veut dire inconnu, jamais gratuit.** La gratuité
 * réelle existe et reste reconnaissable — c'est une remise à zéro sur un tarif
 * de base non nul (`discounted_delivery_charge = 0` avec `delivery_charge > 0`).
 */
export type DeliveryDisplay =
  | { kind: "free"; was: number }
  | { kind: "discounted"; price: number; was: number }
  | { kind: "price"; price: number }
  | { kind: "unknown" };

type FeeCarrier = Pick<
  StoreListItem | StoreDetail,
  "delivery_charge" | "discounted_delivery_charge"
>;

export function deliveryDisplay(store: FeeCarrier | null | undefined): DeliveryDisplay {
  const base = Number(store?.delivery_charge ?? 0);
  const discounted = Number(store?.discounted_delivery_charge ?? base);

  // Aucun tarif de base : l'API ne l'a pas calculé. On se tait.
  if (!Number.isFinite(base) || base <= 0) return { kind: "unknown" };

  if (discounted <= 0) return { kind: "free", was: base };
  if (discounted < base) return { kind: "discounted", price: discounted, was: base };
  return { kind: "price", price: discounted };
}

/**
 * Distance au magasin, en mètres, ou `null` quand elle n'a pas de sens.
 *
 * Zéro n'est pas une distance : c'est ce que renvoie l'API quand on l'interroge
 * depuis les coordonnées du magasin lui-même — ce que fait le rendu serveur des
 * fiches, faute de connaître la position du visiteur. La fiche affichait alors
 * « 0 m », ce qu'aucun client ne peut lire autrement que comme un bug.
 */
export function distanceOrNull(meters: number | null | undefined): number | null {
  const n = Number(meters ?? 0);
  return Number.isFinite(n) && n > 0 ? n : null;
}
